import React, { useState } from 'react';

// Definim interfața pentru props
interface CrowdfundingDetailProps {
  onBack: () => void;
}

// Interfața pentru o inițiativă de crowdfunding
interface CrowdfundingProject {
  id: number;
  titlu: string;
  descriere: string;
  videoUrl: string;
  imagineUrl: string;
  sumaStransa: number;
  sumaTarget: number;
  dataLimita: string;
  zileRamase: number;
  sustinatori: number;
  utilizator: string;
  companie: string;
  taguri: string[];
  optiuniSustinere: SustinereOptiune[];
  sustinatori_lista: Sustinator[];
  actualizari: Actualizare[];
}

// Interfața pentru opțiuni de susținere
interface SustinereOptiune {
  id: number;
  titlu: string;
  descriere: string;
  suma: number;
  beneficii: string[];
  limitata: boolean;
  disponibile?: number;
}

// Interfața pentru susținători
interface Sustinator {
  id: number;
  nume: string;
  suma: number;
  optiuneAleasa: string;
  timestamp: string;
  avatar: string;
  mesaj?: string;
}

// Interfața pentru actualizări
interface Actualizare {
  id: number;
  titlu: string;
  continut: string;
  timestamp: string;
}

const CrowdfundingDetail: React.FC<CrowdfundingDetailProps> = ({ onBack }) => {
  // Proiect exemplu
  const [project, setProject] = useState<CrowdfundingProject>({
    id: 1,
    titlu: "Centru de inovație pentru antreprenorii din Vrancea",
    descriere: "Acest proiect își propune să creeze un spațiu dedicat inovației și colaborării pentru antreprenorii din regiunea noastră. Centrul va oferi infrastructura necesară pentru dezvoltarea de noi produse și servicii, va facilita mentorat pentru startup-uri și va găzdui evenimente de networking.\n\nSpațiul va include:\n- Zone de coworking moderne și flexibile\n- Sală de conferințe echipată pentru evenimente și workshopuri\n- Laboratoare pentru prototipare și testare\n- Birouri private pentru echipe\n- Zonă de relaxare și networking\n\nFondurile strânse vor fi utilizate pentru amenajarea spațiului, achiziționarea echipamentelor necesare și acoperirea costurilor operaționale pentru primele 6 luni.",
    videoUrl: "https://www.example.com/video.mp4",
    imagineUrl: "/crowdfunding.png",
    sumaStransa: 35000,
    sumaTarget: 75000,
    dataLimita: "15 mai 2025",
    zileRamase: 55,
    sustinatori: 47,
    utilizator: "Asociația Antreprenorilor din Vrancea",
    companie: "Patronat Vrancea",
    taguri: ["inovație", "startup", "coworking", "mentorat"],
    optiuniSustinere: [
      {
        id: 1,
        titlu: "Pe Saracie",
        descriere: "Susținere de bază pentru proiect",
        suma: 100,
        beneficii: [
          "Mulțumiri pe pagina proiectului",
          "Invitație la evenimentul de deschidere",
          "Newsletter dedicat cu actualizări despre proiect"
        ],
        limitata: false
      },
      {
        id: 2,
        titlu: "Sunt Om Serios",
        descriere: "Susținere intermediară cu beneficii exclusive",
        suma: 500,
        beneficii: [
          "Toate beneficiile nivelului anterior",
          "Acces gratuit la spațiul de coworking timp de 1 lună",
          "Logo-ul afișat pe wall of fame în centru",
          "Invitație VIP la toate evenimentele din primele 3 luni"
        ],
        limitata: false
      },
      {
        id: 3,
        titlu: "The Big Power",
        descriere: "Susținere majoră cu beneficii premium",
        suma: 1000,
        beneficii: [
          "Toate beneficiile nivelurilor anterioare",
          "Acces gratuit la spațiul de coworking timp de 3 luni",
          "Sesiune de mentorat personalizată cu un antreprenor de succes",
          "Prezentare exclusivă a afacerii tale în cadrul unui eveniment dedicat",
          "Plăcuță personalizată pe unul dintre birourile din centru"
        ],
        limitata: true,
        disponibile: 8
      }
    ],
    sustinatori_lista: [
      {
        id: 1,
        nume: "Elena Dragomir",
        suma: 1000,
        optiuneAleasa: "The Big Power",
        timestamp: "acum 2 zile",
        avatar: "ED",
        mesaj: "Sunt încântată să susțin acest proiect care va aduce plus valoare comunității noastre de afaceri!"
      },
      {
        id: 2,
        nume: "Mihai Constantinescu",
        suma: 500,
        optiuneAleasa: "Sunt Om Serios",
        timestamp: "acum 3 zile",
        avatar: "MC"
      },
      {
        id: 3,
        nume: "SC Vinex SRL",
        suma: 1000,
        optiuneAleasa: "The Big Power",
        timestamp: "acum 4 zile",
        avatar: "VX"
      },
      {
        id: 4,
        nume: "Alexandru Popa",
        suma: 100,
        optiuneAleasa: "Pe saracie",
        timestamp: "acum 5 zile",
        avatar: "AP",
        mesaj: "Un proiect necesar pentru Vrancea!"
      },
      {
        id: 5,
        nume: "Maria Ionescu",
        suma: 100,
        optiuneAleasa: "Pe Saracie",
        timestamp: "acum 6 zile",
        avatar: "MI"
      }
    ],
    actualizari: [
      {
        id: 1,
        titlu: "Am găsit locația perfectă!",
        continut: "Suntem bucuroși să anunțăm că am identificat spațiul ideal pentru centrul nostru de inovație! Este situat central, are 450mp și oferă toate facilitățile de care avem nevoie. Mulțumim tuturor pentru susținere!",
        timestamp: "acum 7 zile"
      },
      {
        id: 2,
        titlu: "Primii parteneri confirmați",
        continut: "Trei companii importante din regiune și-au confirmat susținerea ca parteneri strategici ai centrului. Acest lucru ne va ajuta să oferim servicii de mentorat de înaltă calitate.",
        timestamp: "acum 14 zile"
      }
    ]
  });

  // State pentru opțiunea selectată
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  
  // Calculăm progresul
  const progressPercentage = Math.min(Math.round((project.sumaStransa / project.sumaTarget) * 100), 100);
  
  // Functie pentru sustinere
  const handleSupport = () => {
    if (selectedOption === null) return;
    
    const optiuneAleasa = project.optiuniSustinere.find(opt => opt.id === selectedOption);
    if (!optiuneAleasa) return;
    
    // Actualizăm suma strânsă și numărul de susținători
    setProject(prev => ({
      ...prev,
      sumaStransa: prev.sumaStransa + optiuneAleasa.suma,
      sustinatori: prev.sustinatori + 1,
      // Actualizăm disponibilitatea pentru opțiunile limitate
      optiuniSustinere: prev.optiuniSustinere.map(opt => 
        opt.id === selectedOption && opt.limitata && opt.disponibile 
          ? {...opt, disponibile: opt.disponibile - 1} 
          : opt
      ),
      // Adăugăm susținătorul în listă
      sustinatori_lista: [
        {
          id: prev.sustinatori_lista.length + 1,
          nume: "Tu",
          suma: optiuneAleasa.suma,
          optiuneAleasa: optiuneAleasa.titlu,
          timestamp: "chiar acum",
          avatar: "TU"
        },
        ...prev.sustinatori_lista
      ]
    }));
    
    // Resetăm selecția
    setSelectedOption(null);
    
    // Aici ar trebui să se deschidă un flux de plată real
    alert(`Mulțumim pentru susținere! Ai ales opțiunea "${optiuneAleasa.titlu}" în valoare de ${optiuneAleasa.suma} lei.`);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header cu navigare înapoi */}
      <div className="p-4 border-b flex items-center">
        <button 
          onClick={onBack}
          className="mr-3 text-gray-600 hover:text-blue-600"
        >
          ← Înapoi
        </button>
        <h1 className="text-xl font-semibold">Inițiativă de Crowdfunding</h1>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Partea stângă - Detalii proiect */}
        <div className="w-full md:w-2/3 p-4">
          {/* Titlu proiect */}
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{project.titlu}</h2>
          
          {/* Video sau imagine */}
          <div className="mb-6 rounded-lg overflow-hidden bg-gray-200">
            <img 
              src={project.imagineUrl} 
              alt={project.titlu} 
              className="w-full h-auto object-cover"
            />
          </div>
          
          {/* Progres */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="font-bold text-2xl">{project.sumaStransa} lei</span>
              <span className="text-gray-600">din {project.sumaTarget} lei țintă</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{width: `${progressPercentage}%`}}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{project.sustinatori} susținători</span>
              <span>{project.zileRamase} zile rămase</span>
            </div>
          </div>
          
          {/* Taguri */}
          <div className="flex flex-wrap gap-2 mb-6">
            {project.taguri.map((tag, index) => (
              <a 
                key={index} 
                href="#" 
                className="px-2 py-1 bg-gray-100 text-xs rounded-md hover:bg-gray-200"
              >
                {tag}
              </a>
            ))}
          </div>
          
          {/* Descriere */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-3">Despre proiect</h3>
            <div className="text-gray-700 whitespace-pre-line">
              {project.descriere}
            </div>
          </div>
          
          {/* Actualizări */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-3">Actualizări</h3>
            {project.actualizari.map(update => (
              <div key={update.id} className="border-b pb-4 mb-4 last:border-b-0 last:mb-0">
                <div className="flex justify-between mb-2">
                  <h4 className="font-medium text-lg">{update.titlu}</h4>
                  <span className="text-sm text-gray-500">{update.timestamp}</span>
                </div>
                <p className="text-gray-700">{update.continut}</p>
              </div>
            ))}
          </div>
          
          {/* Susținători */}
          <div>
            <h3 className="text-xl font-semibold mb-3">Susținători ({project.sustinatori})</h3>
            <div className="space-y-4">
              {project.sustinatori_lista.map(sustinator => (
                <div key={sustinator.id} className="flex items-start">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold mr-3 flex-shrink-0">
                    {sustinator.avatar}
                  </div>
                  <div>
                    <div className="flex items-baseline">
                      <span className="font-medium">{sustinator.nume}</span>
                      <span className="ml-2 text-sm text-gray-500">{sustinator.timestamp}</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      A contribuit cu <span className="font-semibold">{sustinator.suma} lei</span> - {sustinator.optiuneAleasa}
                    </p>
                    {sustinator.mesaj && (
                      <p className="mt-1 text-gray-700 italic">"{sustinator.mesaj}"</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Partea dreaptă - Opțiuni de susținere */}
        <div className="w-full md:w-1/3 p-4 bg-gray-50 rounded-r-lg">
          <div className="sticky top-4">
            <h3 className="text-xl font-semibold mb-4">Susține acest proiect</h3>
            
            <div className="space-y-4 mb-6">
              {project.optiuniSustinere.map(optiune => (
                <div 
                  key={optiune.id}
                  className={`border rounded-lg p-4 transition-all cursor-pointer
                    ${selectedOption === optiune.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                    ${optiune.limitata && optiune.disponibile === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  onClick={() => {
                    if (!(optiune.limitata && optiune.disponibile === 0)) {
                      setSelectedOption(optiune.id);
                    }
                  }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-lg">{optiune.titlu}</h4>
                    <span className="font-bold text-lg">{optiune.suma} lei</span>
                  </div>
                  <p className="text-gray-600 mb-3">{optiune.descriere}</p>
                  
                  {optiune.limitata && (
                    <div className="text-sm text-gray-500 mb-3">
                      {optiune.disponibile === 0 
                        ? <span className="text-red-500">Indisponibil - toate locurile ocupate</span>
                        : <span>Limitat: {optiune.disponibile} disponibile</span>
                      }
                    </div>
                  )}
                  
                  <div className="mt-2">
                    <h5 className="font-medium mb-1">Beneficii:</h5>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600 text-sm">
                      {optiune.beneficii.map((beneficiu, index) => (
                        <li key={index}>{beneficiu}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={selectedOption === null}
              onClick={handleSupport}
            >
              Susține proiectul
            </button>
            
            <div className="mt-4 text-sm text-gray-500 text-center">
              <p>Proiect inițiat de</p>
              <p className="font-medium">{project.utilizator}</p>
              <p>{project.companie}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrowdfundingDetail;